import gsap from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(MotionPathPlugin, ScrollTrigger);

const reducedMotionQuery = '(prefers-reduced-motion: reduce)';

const prefersReducedMotion = (): boolean =>
    window.matchMedia(reducedMotionQuery).matches;

const scrollToSection = (target: HTMLElement, reducedMotion: boolean): void => {
    const startY = window.scrollY;
    const scrollMarginTop = parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
    const targetY = Math.max(0, startY + target.getBoundingClientRect().top - scrollMarginTop);

    if (reducedMotion) {
        window.scrollTo(0, targetY);
        return;
    }

    window.scrollTo({
        top: targetY,
        behavior: 'smooth'
    });
};

const clamp = (value: number): number =>
    Math.min(1, Math.max(0, value));

const getCssVar = (element: HTMLElement, name: string, fallback: string): string => {
    const value = window.getComputedStyle(element).getPropertyValue(name).trim();
    return value.length > 0 ? value : fallback;
};

const parseRgb = (value: string): [number, number, number] => {
    const channels = value
        .split(',')
        .map(channel => Number.parseFloat(channel.trim()))
        .filter(channel => Number.isFinite(channel));

    if (channels.length < 3) {
        return [237, 41, 57];
    }

    return [
        Math.round(channels[0]),
        Math.round(channels[1]),
        Math.round(channels[2])
    ];
};

const mixRgb = (
    from: [number, number, number],
    to: [number, number, number],
    progress: number
): [number, number, number] => {
    const normalized = clamp(progress);
    const eased = normalized * normalized * (3 - 2 * normalized);

    return [
        Math.round(from[0] + (to[0] - from[0]) * eased),
        Math.round(from[1] + (to[1] - from[1]) * eased),
        Math.round(from[2] + (to[2] - from[2]) * eased)
    ];
};

const setRgbVar = (element: HTMLElement, name: string, rgb: [number, number, number]): void => {
    element.style.setProperty(name, `${rgb[0]}, ${rgb[1]}, ${rgb[2]}`);
};

const storyBlackRgb: [number, number, number] = [5, 5, 6];

const setText = (root: HTMLElement, selector: string, value: string | undefined): void => {
    const element = root.querySelector<HTMLElement>(selector);
    if (element && value !== undefined && element.textContent !== value) {
        element.textContent = value;
    }
};

const getOrderedSections = (sections: HTMLElement[]): HTMLElement[] =>
    [...sections].sort((a, b) => {
        if (a === b) {
            return 0;
        }

        return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });

const initServicesHeroEntrance = (): void => {
    const hero = document.querySelector<HTMLElement>('.svc-hero');
    if (!hero || hero.dataset.heroEntranceStarted === 'true') {
        return;
    }

    hero.dataset.heroEntranceStarted = 'true';

    const revealItems = Array.from(hero.querySelectorAll<HTMLElement>('[data-services-hero-reveal]'));
    const visual = hero.querySelector<HTMLElement>('.svc-hero__arch');

    if (prefersReducedMotion()) {
        revealItems.forEach(item => item.classList.add('is-visible'));
        return;
    }

    const animatedItems = [...revealItems, ...(visual ? [visual] : [])];
    gsap.killTweensOf(animatedItems);
    gsap.set(revealItems, { autoAlpha: 0, y: 30, filter: 'blur(10px)' });

    if (visual) {
        gsap.set(visual, { autoAlpha: 0, y: 18, scale: 0.985, filter: 'blur(8px)' });
    }

    const settle = (): void => {
        gsap.set(revealItems, {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            clearProps: 'filter,opacity,visibility,transform'
        });

        if (visual) {
            gsap.set(visual, {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                filter: 'blur(0px)',
                clearProps: 'filter,opacity,visibility,transform'
            });
        }
    };

    gsap.timeline({ defaults: { ease: 'power3.out' }, onComplete: settle })
        .to(revealItems, {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: 0.78,
            stagger: 0.095
        }, 0.08)
        .to(visual, {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            filter: 'blur(0px)',
            duration: 1.05
        }, 0.18);

    window.setTimeout(settle, 1800);
};

export function initServicesHero(): void {
    initServicesHeroEntrance();

    const mesh = document.getElementById('arch-mesh-svc');
    if (!mesh) return;
    const svgLayer = mesh.querySelector<SVGElement>('.arch-mesh__svg');
    if (!svgLayer) return;
    if (prefersReducedMotion()) return;

    const scenarios = [
        {
            nodes: ['analysis', 'consulting', 'design'],
            paths: ['#svc-path-entry-1', '#svc-path-entry-2'],
            speed: 3
        },
        {
            nodes: ['design', 'dev', 'ai', 'auto', 'cyber', 'devops'],
            paths: ['#svc-path-fan-ai', '#svc-path-fan-dev', '#svc-path-fan-sec', '#svc-path-fan-ops', '#svc-path-fan-auto'],
            speed: 2
        },
        {
            nodes: ['ai', 'dev', 'cyber', 'devops', 'auto', 'cloud', 'success'],
            paths: ['#svc-path-in-ai', '#svc-path-in-dev', '#svc-path-in-sec', '#svc-path-in-ops', '#svc-path-in-auto', '#svc-path-end'],
            speed: 2.5
        }
    ];

    let currentIdx = 0;
    let particleTweens: gsap.core.Tween[] = [];
    let autoTimer: number | null = null;

    const clearScenario = (): void => {
        gsap.to(mesh.querySelectorAll('.sys-node'), { opacity: 1, scale: 1, duration: 0.4 });
        gsap.to(mesh.querySelectorAll('.mesh-path'), { opacity: 0.15, stroke: 'var(--line)', strokeWidth: 1.5, duration: 0.4 });
        particleTweens.forEach(tween => tween.kill());
        particleTweens = [];
        svgLayer.querySelectorAll('.svc-packet').forEach(packet => packet.remove());
    };

    const playScenario = (index: number): void => {
        if (autoTimer !== null) {
            window.clearTimeout(autoTimer);
            autoTimer = null;
        }

        clearScenario();
        const scenario = scenarios[index];

        scenario.nodes.forEach(nodeId => {
            const node = mesh.querySelector(`[data-id="${nodeId}"]`);
            if (!node) {
                return;
            }

            const isGold = nodeId === 'success' || nodeId === 'consulting';
            gsap.to(node, {
                borderColor: isGold ? '#d4af37' : 'var(--accent)',
                backgroundColor: 'rgba(20,20,20,0.98)',
                boxShadow: isGold
                    ? '0 0 30px rgba(212,175,55,.36), 0 15px 35px rgba(0,0,0,.56)'
                    : '0 0 30px rgba(237,41,57,.38), 0 15px 35px rgba(0,0,0,.56)',
                duration: 0.6
            });
        });

        scenario.paths.forEach(selector => {
            const path = mesh.querySelector(selector);
            if (path) {
                gsap.to(path, { opacity: 0.82, stroke: 'var(--accent)', strokeWidth: 2.4, duration: 0.6 });
            }
        });

        scenario.paths.forEach((selector, pathIndex) => {
            const path = mesh.querySelector<SVGPathElement>(selector);
            if (!path) {
                return;
            }

            for (let i = 0; i < 2; i += 1) {
                const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                group.setAttribute('class', 'svc-packet');

                const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                rect.setAttribute('width', '16');
                rect.setAttribute('height', '4');
                rect.setAttribute('rx', '1');
                rect.setAttribute('fill', 'var(--accent)');
                rect.setAttribute('x', '-8');
                rect.setAttribute('y', '-2');

                group.appendChild(rect);
                svgLayer.appendChild(group);

                const tween = gsap.to(group, {
                    motionPath: { path, autoRotate: true, align: path, alignOrigin: [0.5, 0.5] },
                    duration: scenario.speed,
                    repeat: -1,
                    delay: i * (scenario.speed / 2) + pathIndex * 0.3,
                    ease: 'none'
                });

                particleTweens.push(tween);
            }
        });

        autoTimer = window.setTimeout(() => {
            currentIdx = (currentIdx + 1) % scenarios.length;
            playScenario(currentIdx);
        }, 8000);
    };

    gsap.set(mesh.querySelectorAll('.sys-node'), { scale: 0, autoAlpha: 0 });
    gsap.timeline({ onComplete: () => playScenario(0) })
        .from(mesh.querySelector('.arch-mesh__grid'), { opacity: 0, duration: 1.2, ease: 'power2.out' })
        .to(mesh.querySelectorAll('.sys-node'), {
            autoAlpha: 1,
            scale: 1,
            duration: 1.05,
            stagger: { amount: 0.85, from: 'start' },
            ease: 'back.out(1.35)'
        }, '-=0.9');

    mesh.querySelectorAll('.sys-node').forEach(node => {
        node.addEventListener('mouseenter', () => {
            if (autoTimer !== null) {
                window.clearTimeout(autoTimer);
                autoTimer = null;
            }

            clearScenario();
            gsap.to(node, { borderColor: '#fff', scale: 1.08, duration: 0.3 });
        });

        node.addEventListener('mouseleave', () => {
            gsap.to(node, { scale: 1, duration: 0.3 });
            playScenario(currentIdx);
        });
    });
}

const mobileVisualBreakpoint = '(max-width: 900px)';
const mobileVisualBaseWidth = 580;

const clearMobileVisualScale = (visual: HTMLElement): void => {
    visual.style.removeProperty('--svc-mobile-visual-base');
    visual.style.removeProperty('--svc-mobile-visual-height');
    visual.style.removeProperty('--svc-mobile-visual-scale');
};

const updateMobileVisualScales = (root: HTMLElement): void => {
    const shouldScale = window.matchMedia(mobileVisualBreakpoint).matches;
    const visuals = Array.from(root.querySelectorAll<HTMLElement>('.svc-section__mobile-media .svc-hero-visual'));

    visuals.forEach(visual => {
        const content = visual.firstElementChild;

        if (!(content instanceof HTMLElement)) {
            return;
        }

        if (!shouldScale) {
            clearMobileVisualScale(visual);
            return;
        }

        visual.style.setProperty('--svc-mobile-visual-base', `${mobileVisualBaseWidth}px`);
        visual.style.setProperty('--svc-mobile-visual-scale', '1');
        visual.style.removeProperty('--svc-mobile-visual-height');

        const availableWidth = Math.max(1, visual.clientWidth);
        const contentRect = content.getBoundingClientRect();
        const contentWidth = Math.max(
            mobileVisualBaseWidth,
            content.scrollWidth,
            Math.ceil(contentRect.width)
        );
        const contentHeight = Math.max(
            content.scrollHeight,
            content.offsetHeight,
            Math.ceil(contentRect.height)
        );
        const scale = Math.min(1, availableWidth / contentWidth);

        visual.style.setProperty('--svc-mobile-visual-base', `${contentWidth}px`);
        visual.style.setProperty('--svc-mobile-visual-scale', scale.toFixed(4));
        visual.style.setProperty('--svc-mobile-visual-height', `${Math.ceil((contentHeight + 4) * scale)}px`);
    });
};

const initServicesMobileVisualScales = (root: HTMLElement): void => {
    if (root.dataset.mobileVisualScalesReady === 'true') {
        return;
    }

    root.dataset.mobileVisualScalesReady = 'true';

    let updateQueued = false;
    const requestUpdate = (): void => {
        if (updateQueued) {
            return;
        }

        updateQueued = true;
        window.requestAnimationFrame(() => {
            updateQueued = false;
            updateMobileVisualScales(root);
            ScrollTrigger.refresh();
        });
    };

    updateMobileVisualScales(root);
    window.addEventListener('load', requestUpdate, { once: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('orientationchange', requestUpdate, { passive: true });
};

const initServicesStory = (root: HTMLElement): void => {
    const story = root.querySelector<HTMLElement>('[data-services-story]');
    const serviceSteps = Array.from(root.querySelectorAll<HTMLElement>('[data-service-step]'));
    const specialSections = Array.from(root.querySelectorAll<HTMLElement>('[data-services-special]'));
    const sections = getOrderedSections([...specialSections, ...serviceSteps]);
    const stageSections = sections.filter(section => Boolean(section.dataset.stageVisual));
    const visuals = Array.from(root.querySelectorAll<HTMLElement>('[data-service-stage-visual]'));
    const progress = root.querySelector<HTMLElement>('[data-services-progress]');

    if (sections.length === 0) {
        return;
    }

    const reducedMotion = prefersReducedMotion();
    let activeSection: HTMLElement | null = null;
    let activeStageSection: HTMLElement | null = null;
    let activeIndex = -1;
    let activeStageIndex = -1;
    let viewportUpdateQueued = false;
    const stageTones = stageSections.map(section =>
        parseRgb(getCssVar(section, '--svc-accent-rgb', '237, 41, 57'))
    );

    const setRootTone = (section: HTMLElement): void => {
        root.style.setProperty('--svc-active-accent', getCssVar(section, '--svc-accent', '#ed2939'));
        root.style.setProperty('--svc-active-rgb', getCssVar(section, '--svc-accent-rgb', '237, 41, 57'));
        root.dataset.activeTone = section.dataset.stageVisual ?? section.id;
    };

    const updateStoryBackground = (): void => {
        if (!story) {
            return;
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const storyRect = story.getBoundingClientRect();
        const viewportAnchor = window.scrollY + viewportHeight * 0.52;
        const depth = clamp((viewportHeight * 1.12 - storyRect.top) / (viewportHeight * 1.1));
        const rise = clamp((viewportHeight - storyRect.top) / (viewportHeight * 0.92));
        const lastStage = stageSections[stageSections.length - 1];
        let exitFade = 0;

        if (lastStage && story.contains(lastStage)) {
            const lastRect = lastStage.getBoundingClientRect();
            const lastTop = window.scrollY + lastRect.top;
            const lastHeight = Math.max(1, lastRect.height);
            const fadeStart = lastTop + lastHeight * 0.64;
            const fadeEnd = lastTop + lastHeight * 0.96;

            exitFade = clamp((viewportAnchor - fadeStart) / Math.max(1, fadeEnd - fadeStart));
        }

        const colorPresence = 1 - exitFade;

        serviceSteps.forEach(step => {
            if (!story.contains(step)) {
                return;
            }

            const rect = step.getBoundingClientRect();
            const sectionTop = window.scrollY + rect.top;
            const sectionHeight = Math.max(1, rect.height);
            const sectionCenter = sectionTop + sectionHeight * 0.5;
            const glowRange = Math.max(viewportHeight * 0.72, sectionHeight * 0.44);
            const glowFalloff = 1 - clamp(Math.abs(viewportAnchor - sectionCenter) / glowRange);
            const glow = Math.pow(glowFalloff, 1.45);

            step.style.setProperty('--svc-section-glow', glow.toFixed(4));
        });

        root.style.setProperty('--svc-story-depth', depth.toFixed(4));
        root.style.setProperty('--svc-story-exit', exitFade.toFixed(4));
        root.style.setProperty('--svc-story-rise', (rise * colorPresence * 0.24).toFixed(4));
        root.style.setProperty('--svc-story-lift', `${((1 - rise) * 16).toFixed(3)}vh`);
        root.style.setProperty('--svc-story-accent-top', (depth * colorPresence * 0.025).toFixed(4));
        root.style.setProperty('--svc-story-red-top', '0');
        root.style.setProperty('--svc-story-base-soft', Math.max(depth * 0.46, exitFade * 0.96).toFixed(4));
        root.style.setProperty('--svc-story-base-mid', Math.max(depth * 0.62, exitFade * 0.99).toFixed(4));
        root.style.setProperty('--svc-story-base-strong', Math.max(depth * 0.76, exitFade).toFixed(4));

        if (stageSections.length === 0 || stageTones.length === 0) {
            return;
        }

        const centers = stageSections.map(section => {
            const rect = section.getBoundingClientRect();
            return window.scrollY + rect.top + rect.height * 0.5;
        });

        const applyStoryTone = (rgb: [number, number, number]): void => {
            setRgbVar(root, '--svc-story-bg-rgb', mixRgb(rgb, storyBlackRgb, exitFade));
        };

        if (viewportAnchor <= centers[0]) {
            applyStoryTone(stageTones[0]);
            return;
        }

        const lastIndex = centers.length - 1;

        if (viewportAnchor >= centers[lastIndex]) {
            applyStoryTone(stageTones[lastIndex]);
            return;
        }

        for (let index = 0; index < lastIndex; index += 1) {
            const start = centers[index];
            const end = centers[index + 1];

            if (viewportAnchor < start || viewportAnchor > end) {
                continue;
            }

            applyStoryTone(mixRgb(stageTones[index], stageTones[index + 1], (viewportAnchor - start) / Math.max(1, end - start)));
            return;
        }
    };

    const settleAnimatedItems = (items: HTMLElement[]): void => {
        gsap.killTweensOf(items);
        gsap.set(items, {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            clearProps: 'filter,opacity,visibility,transform'
        });
    };

    const revealItems = (items: HTMLElement[], direction: number, animate: boolean): void => {
        if (items.length === 0) {
            return;
        }

        gsap.killTweensOf(items);

        if (!animate || reducedMotion) {
            settleAnimatedItems(items);
            return;
        }

        gsap.fromTo(
            items,
            { autoAlpha: 0, y: direction * 26, filter: 'blur(9px)' },
            {
                autoAlpha: 1,
                y: 0,
                filter: 'blur(0px)',
                duration: 0.58,
                stagger: 0.055,
                ease: 'power3.out',
                overwrite: 'auto',
                clearProps: 'filter,opacity,visibility,transform'
            }
        );

        window.setTimeout(() => settleAnimatedItems(items), 820);
    };

    const setStageVisual = (section: HTMLElement): HTMLElement | null => {
        const visualKey = section.dataset.stageVisual;
        let activeVisual: HTMLElement | null = null;

        visuals.forEach(visual => {
            const isActive = visual.dataset.serviceStageVisual === visualKey;
            visual.classList.toggle('is-active', isActive);
            if (isActive) {
                activeVisual = visual;
            }
        });

        return activeVisual;
    };

    const animateStage = (section: HTMLElement, direction: number): void => {
        if (reducedMotion) {
            return;
        }

        const stageItems = [
            root.querySelector<HTMLElement>('[data-services-stage-kicker]'),
            root.querySelector<HTMLElement>('[data-services-stage-backdrop]')
        ].filter((item): item is HTMLElement => Boolean(item));

        gsap.killTweensOf(stageItems);
        gsap.fromTo(
            stageItems,
            { autoAlpha: 0, y: direction * 18, filter: 'blur(10px)' },
            {
                autoAlpha: 1,
                y: 0,
                filter: 'blur(0px)',
                duration: 0.46,
                stagger: 0.035,
                ease: 'power3.out',
                overwrite: 'auto',
                clearProps: 'filter,opacity,visibility,transform'
            }
        );

        window.setTimeout(() => {
            if (activeStageSection !== section) {
                return;
            }

            settleAnimatedItems(stageItems);
        }, 720);
    };

    const activateStage = (section: HTMLElement, animate = true): void => {
        if (!section.dataset.stageVisual) {
            return;
        }

        const nextStageIndex = Math.max(0, stageSections.indexOf(section));
        const direction = activeStageIndex === -1 || nextStageIndex >= activeStageIndex ? 1 : -1;

        if (activeStageSection === section) {
            return;
        }

        activeStageSection = section;
        activeStageIndex = nextStageIndex;

        setStageVisual(section);
        setText(root, '[data-services-stage-kicker]', section.dataset.stageKicker);
        setText(root, '[data-services-stage-backdrop]', section.dataset.stageBackdrop ?? section.dataset.stageKicker);

        if (animate) {
            animateStage(section, direction);
        }
    };

    const revealSection = (section: HTMLElement, direction: number, animate: boolean): void => {
        if (section.matches('.svc-hero')) {
            initServicesHeroEntrance();
            return;
        }

        const selector = section.matches('[data-service-step]')
            ? '[data-service-story-reveal]'
            : '[data-service-special-reveal]';

        revealItems(Array.from(section.querySelectorAll<HTMLElement>(selector)), direction, animate);
    };

    const activate = (section: HTMLElement, animate = true): void => {
        const nextIndex = Math.max(0, sections.indexOf(section));
        const direction = activeIndex === -1 || nextIndex >= activeIndex ? 1 : -1;

        if (activeSection === section) {
            return;
        }

        activeSection = section;
        activeIndex = nextIndex;

        sections.forEach(candidate => candidate.classList.toggle('is-active', candidate === section));
        setRootTone(section);
        root.classList.toggle('is-services-stage-active', Boolean(section.dataset.stageVisual));

        activateStage(section, animate);
        revealSection(section, direction, animate);
    };

    const updateActiveFromViewport = (): void => {
        updateStoryBackground();

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const viewportCenter = viewportHeight * 0.5;
        let bestSection: HTMLElement | null = null;
        let bestScore = Number.POSITIVE_INFINITY;

        sections.forEach(section => {
            const rect = section.getBoundingClientRect();
            if (rect.bottom <= 0 || rect.top >= viewportHeight) {
                return;
            }

            const visibleTop = Math.max(0, rect.top);
            const visibleBottom = Math.min(viewportHeight, rect.bottom);
            const visibleCenter = (visibleTop + visibleBottom) / 2;
            const visibleHeight = Math.max(1, visibleBottom - visibleTop);
            const score = Math.abs(visibleCenter - viewportCenter) - visibleHeight * 0.08;

            if (score < bestScore) {
                bestScore = score;
                bestSection = section;
            }
        });

        if (bestSection) {
            activate(bestSection);
        }
    };

    const requestViewportUpdate = (): void => {
        if (viewportUpdateQueued) {
            return;
        }

        viewportUpdateQueued = true;
        window.requestAnimationFrame(() => {
            viewportUpdateQueued = false;
            updateActiveFromViewport();
        });
    };

    const revealImmediately = (): void => {
        root.querySelectorAll<HTMLElement>('[data-service-story-reveal], [data-service-special-reveal], [data-services-hero-reveal]').forEach(item => {
            item.classList.add('is-visible');
            gsap.set(item, {
                autoAlpha: 1,
                y: 0,
                filter: 'blur(0px)',
                clearProps: 'filter,opacity,visibility,transform'
            });
        });
    };

    const initialHash = decodeURIComponent(window.location.hash.replace('#', '').trim());
    const initialSection = initialHash
        ? sections.find(section =>
            section.id === initialHash ||
            section.dataset.stageVisual === initialHash
        ) ?? sections[0]
        : sections[0];

    if (reducedMotion) {
        root.classList.add('is-reduced-motion');
        activate(initialSection, false);
        updateStoryBackground();
        revealImmediately();
        progress?.style.setProperty('transform', 'scaleY(1)');
    } else {
        const revealTargets = Array.from(root.querySelectorAll<HTMLElement>('[data-service-story-reveal], [data-service-special-reveal]'));
        gsap.set(revealTargets, { autoAlpha: 0, y: 28, filter: 'blur(10px)' });

        activate(initialSection, false);

        specialSections.forEach(section => {
            ScrollTrigger.create({
                trigger: section,
                start: 'top 58%',
                end: 'bottom 42%',
                onToggle: self => {
                    if (self.isActive) {
                        activate(section);
                    }
                },
                onEnter: () => activate(section),
                onEnterBack: () => activate(section)
            });
        });

        if (story && serviceSteps.length > 0) {
            ScrollTrigger.create({
                trigger: story,
                start: 'top 84%',
                end: 'bottom 18%',
                onEnter: () => root.classList.add('is-story-visible'),
                onEnterBack: () => root.classList.add('is-story-visible'),
                onLeave: () => root.classList.remove('is-story-visible'),
                onLeaveBack: () => root.classList.remove('is-story-visible')
            });
        }

        serviceSteps.forEach((step, index) => {
            ScrollTrigger.create({
                trigger: step,
                start: index === 0 ? 'top 70%' : 'top 58%',
                end: 'bottom 42%',
                onToggle: self => {
                    if (self.isActive) {
                        activate(step);
                    }
                },
                onEnter: () => activate(step),
                onEnterBack: () => activate(step)
            });
        });

        if (story && stageSections.length > 1) {
            ScrollTrigger.create({
                trigger: story,
                start: 'top top',
                end: 'bottom bottom',
                snap: {
                    snapTo: progressValue => {
                        const snapDistance = Math.max(1, story.offsetHeight - window.innerHeight);
                        const storyTop = story.getBoundingClientRect().top + window.scrollY;
                        const snapPoints = stageSections
                            .filter(section => story.contains(section))
                            .map(section => {
                                const sectionTop = section.getBoundingClientRect().top + window.scrollY;
                                return clamp((sectionTop - storyTop) / snapDistance);
                            });

                        return snapPoints.reduce((nearest, point) =>
                            Math.abs(point - progressValue) < Math.abs(nearest - progressValue) ? point : nearest,
                            snapPoints[0] ?? progressValue
                        );
                    },
                    duration: { min: 0.16, max: 0.34 },
                    delay: 0.08,
                    ease: 'power2.out'
                }
            });
        }

        if (story && progress) {
            ScrollTrigger.create({
                trigger: story,
                start: 'top top',
                end: 'bottom bottom',
                scrub: true,
                onUpdate: self => {
                    progress.style.transform = `scaleY(${Math.max(0.025, clamp(self.progress)).toFixed(4)})`;
                    updateStoryBackground();
                }
            });
        }

        const stage = root.querySelector<HTMLElement>('[data-services-stage]');
        if (story && stage) {
            gsap.to(stage, {
                '--stage-drift': '2.25px',
                ease: 'none',
                scrollTrigger: {
                    trigger: story,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: true
                }
            });
        }
    }

    window.addEventListener('scroll', requestViewportUpdate, { passive: true });
    window.addEventListener('load', () => {
        ScrollTrigger.refresh();
        requestViewportUpdate();
    }, { once: true });
    window.addEventListener('resize', () => {
        ScrollTrigger.refresh();
        requestViewportUpdate();
    }, { passive: true });
};

const initServicesSideNav = (root: HTMLElement): void => {
    const dots = Array.from(document.querySelectorAll<HTMLAnchorElement>('.svc-side-dot'));
    if (dots.length === 0) {
        return;
    }

    const sections = dots
        .map(dot => dot.dataset.target ? document.getElementById(dot.dataset.target) : null)
        .filter((section): section is HTMLElement => section !== null);

    if (sections.length === 0) {
        return;
    }

    const reducedMotion = prefersReducedMotion();

    const setActive = (id: string): void => {
        dots.forEach(dot => {
            const isActive = dot.dataset.target === id;
            dot.classList.toggle('is-active', isActive);

            if (isActive) {
                dot.setAttribute('aria-current', 'location');
            } else {
                dot.removeAttribute('aria-current');
            }
        });
    };

    let ticking = false;

    const updateActive = (): void => {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const viewportCenter = viewportHeight * 0.48;
        let bestMatch: HTMLElement | null = null;
        let bestScore = Number.POSITIVE_INFINITY;

        sections.forEach(section => {
            const rect = section.getBoundingClientRect();
            if (rect.bottom <= 0 || rect.top >= viewportHeight) {
                return;
            }

            const visibleTop = Math.max(0, rect.top);
            const visibleBottom = Math.min(viewportHeight, rect.bottom);
            const visibleCenter = (visibleTop + visibleBottom) / 2;
            const visibleHeight = Math.max(1, visibleBottom - visibleTop);
            const score = Math.abs(visibleCenter - viewportCenter) - visibleHeight * 0.08;

            if (score < bestScore) {
                bestScore = score;
                bestMatch = section;
            }
        });

        setActive(bestMatch?.id ?? sections[0].id);
        ticking = false;
    };

    const requestUpdate = (): void => {
        if (ticking) {
            return;
        }

        ticking = true;
        window.requestAnimationFrame(updateActive);
    };

    dots.forEach(dot => {
        dot.addEventListener('click', event => {
            const targetId = dot.dataset.target;
            const target = targetId ? document.getElementById(targetId) : null;
            if (!targetId || !target) {
                return;
            }

            event.preventDefault();
            setActive(targetId);
            scrollToSection(target, reducedMotion);
            window.history.replaceState(null, '', `#${targetId}`);
        });
    });

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('load', requestUpdate, { once: true });

    const initialHash = decodeURIComponent(window.location.hash.replace('#', '').trim());
    const initialSection = initialHash ? sections.find(section => section.id === initialHash) : null;
    if (initialSection && root.contains(initialSection)) {
        window.setTimeout(() => {
            scrollToSection(initialSection, true);
            setActive(initialSection.id);
        }, 80);
    } else {
        updateActive();
    }
};

export function initServices(): void {
    const root = document.getElementById('servicesPage');
    if (!root) {
        return;
    }

    initServicesMobileVisualScales(root);
    initServicesStory(root);
    initServicesSideNav(root);

    window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
}
